'use client';

import type { ReactNode } from 'react';

import type { SearchLink } from '@eliadmin/core/src/components/nt/header/header-search';
import {
  SearchChips,
  SearchGroup,
  SearchLinkRow,
} from '@eliadmin/core/src/components/nt/header/search/search-ui';
import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';

type SearchSuggestionsProps = {
  printers: SearchLink[];
  categories: SearchLink[];
  articles: SearchLink[];
  /** „Tlačiarne“ – the header search field (the others are core words). */
  printersTitle: string;
};

const noop = () => undefined;

/** Column count of the card by the groups it has – whole class names for Tailwind. */
const COLUMNS = ['', 'lg:grid-cols-1', 'lg:grid-cols-2', 'lg:grid-cols-3'];

function LinkRows({ links, icon }: { links: SearchLink[]; icon: 'printer' | 'article' }) {
  return (
    <ul role="list" className="flex flex-col gap-2">
      {links.map(link => (
        <li key={link.id}>
          <SearchLinkRow
            href={link.href}
            icon={<Icon name={icon} size={20} className="text-[#127A26]" />}
            title={link.name}
            onClick={noop}
          />
        </li>
      ))}
    </ul>
  );
}

/**
 * The groups of the header search panel (Figma 185:14902 – „Tlačiarne“, „Kategórie“, „Poradňa“) as
 * one card above the products: printer models first (the fastest way to the right toner), the
 * matching categories as chips, the articles. Groups without a match are left out and the others
 * share the width – side by side from 1024 px with 1 px lines between, stacked below. Nothing at
 * all without a match.
 */
export function SearchSuggestions({
  printers,
  categories,
  articles,
  printersTitle,
}: SearchSuggestionsProps) {
  const { t } = useTranslations();

  const groups: { key: string; title: string; content: ReactNode }[] = [];
  if (printers.length) {
    groups.push({
      key: 'printers',
      title: printersTitle,
      content: <LinkRows links={printers} icon="printer" />,
    });
  }
  if (categories.length) {
    groups.push({
      key: 'categories',
      title: t('text176'),
      content: (
        <SearchChips
          chips={categories.map(category => ({
            key: category.id,
            label: category.name,
            href: category.href,
          }))}
          onSelect={noop}
        />
      ),
    });
  }
  if (articles.length) {
    groups.push({
      key: 'articles',
      title: t('text475'),
      content: <LinkRows links={articles} icon="article" />,
    });
  }
  if (!groups.length) return null;

  return (
    <div
      className={cn(
        'grid divide-y divide-[#E2E5DF] rounded-2xl border border-[#E2E5DF] lg:divide-x lg:divide-y-0',
        COLUMNS[groups.length],
      )}
    >
      {groups.map(group => (
        <SearchGroup key={group.key} title={group.title} className="gap-3 p-4 sm:p-6">
          {group.content}
        </SearchGroup>
      ))}
    </div>
  );
}
